import axios from 'axios';
import {
  ApiResponse,
  User,
  Event,
  Ticket,
  // Bid, - COMMENTED OUT FOR INITIAL RELEASE
  Purchase,
  DashboardStats,
  LoginRequest,
  RegisterRequest,
  CreateEventRequest,
  CreateTicketRequest,
  // PlaceBidRequest, - COMMENTED OUT FOR INITIAL RELEASE
  PurchaseTicketRequest,
  PaginatedResponse,
  CreatePromoterRequest,
  IssueTicketRequest,
  PromoterDashboardStats
} from '../types';

const API_BASE_URL = 'https://ticketreselling-production.up.railway.app/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Only redirect on 401 errors if we're not already on the login page
    if (error.response?.status === 401 && window.location.pathname !== '/login') {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (data: RegisterRequest): Promise<ApiResponse<{ user: User; token: string }>> =>
    api.post('/auth/register', data).then(res => res.data),

  login: (data: LoginRequest): Promise<ApiResponse<{ user: User; token: string }>> =>
    api.post('/auth/login', data).then(res => res.data),

  getCurrentUser: (): Promise<ApiResponse<User>> =>
    api.get('/auth/me').then(res => res.data),

  verifyEmail: (token: string): Promise<ApiResponse<{ message: string }>> =>
    api.get(`/auth/verify-email?token=${token}`).then(res => res.data),

  resendVerification: (email: string): Promise<ApiResponse<{ message: string }>> =>
    api.post('/auth/resend-verification', { email }).then(res => res.data),



  updateProfile: (data: Partial<User>): Promise<ApiResponse<{ user: User }>> =>
    api.put('/auth/profile', data).then(res => res.data),

  changePassword: (data: { currentPassword: string; newPassword: string }): Promise<ApiResponse<{ message: string }>> =>
    api.put('/auth/change-password', data).then(res => res.data),
};

// Events API
export const eventsAPI = {
  getAll: (params?: { page?: number; limit?: number; category?: string; search?: string; resaleAllowed?: boolean }): Promise<ApiResponse<PaginatedResponse<Event>>> =>
    api.get('/events', { params }).then(res => res.data),

  getById: (id: string): Promise<ApiResponse<Event>> =>
    api.get(`/events/${id}`).then(res => res.data),

  create: (data: CreateEventRequest): Promise<ApiResponse<Event>> =>
    api.post('/events', data).then(res => res.data),

  addMissing: (data: CreateEventRequest): Promise<ApiResponse<Event>> =>
    api.post('/events/add-missing', data).then(res => res.data),

  update: (id: string, data: Partial<CreateEventRequest>): Promise<ApiResponse<Event>> =>
    api.put(`/events/${id}`, data).then(res => res.data),

  delete: (id: string): Promise<ApiResponse<void>> =>
    api.delete(`/events/${id}`).then(res => res.data),

  getCategories: (): Promise<ApiResponse<string[]>> =>
    api.get('/events/categories').then(res => res.data),
};

// Tickets API
export const ticketsAPI = {
  getAll: (params?: { page?: number; limit?: number; status?: string; eventId?: string }): Promise<ApiResponse<PaginatedResponse<Ticket>>> =>
    api.get('/tickets', { params }).then(res => res.data),

  getById: (id: string): Promise<ApiResponse<Ticket>> =>
    api.get(`/tickets/${id}`).then(res => res.data),

  create: (data: CreateTicketRequest): Promise<ApiResponse<Ticket>> =>
    api.post('/tickets', data).then(res => res.data),

  createWithFile: (formData: FormData): Promise<ApiResponse<Ticket>> =>
    api.post('/tickets', formData).then(res => res.data),

  update: (id: string, data: Partial<CreateTicketRequest>): Promise<ApiResponse<Ticket>> =>
    api.put(`/tickets/${id}`, data).then(res => res.data),

  delete: (id: string): Promise<ApiResponse<void>> =>
    api.delete(`/tickets/${id}`).then(res => res.data),

  purchase: (id: string, data: PurchaseTicketRequest): Promise<ApiResponse<Purchase>> =>
    api.post(`/tickets/${id}/purchase`, data).then(res => res.data),

  getMyTickets: (): Promise<ApiResponse<Ticket[]>> =>
    api.get('/tickets/my').then(res => res.data),

  getMyListings: (): Promise<ApiResponse<Ticket[]>> =>
    api.get('/tickets/my-listings').then(res => res.data),

  getMyAllTickets: (): Promise<ApiResponse<Ticket[]>> =>
    api.get('/tickets/my-all-tickets').then(res => res.data),



  approveTicket: (id: string): Promise<ApiResponse<Ticket>> =>
    api.put(`/tickets/${id}/approve`).then(res => res.data),

  rejectTicket: (id: string, reason: string): Promise<ApiResponse<Ticket>> =>
    api.put(`/tickets/${id}/reject`, { reason }).then(res => res.data),

  completeTransaction: (id: string): Promise<ApiResponse<Ticket>> =>
    api.put(`/tickets/${id}/complete-transaction`).then(res => res.data),

  cancelListing: (id: string): Promise<ApiResponse<Ticket>> =>
    api.put(`/tickets/${id}/cancel-listing`).then(res => res.data),
};

// Bids API - COMMENTED OUT FOR INITIAL RELEASE
/*
export const bidsAPI = {
  placeBid: (ticketId: string, data: PlaceBidRequest): Promise<ApiResponse<Bid>> =>
    api.post(`/bids/${ticketId}`, data).then(res => res.data),

  getAuctionStatus: (ticketId: string): Promise<ApiResponse<any>> =>
    api.get(`/bids/ticket/${ticketId}`).then(res => res.data),

  getTicketBids: (ticketId: string): Promise<ApiResponse<Bid[]>> =>
    api.get(`/bids/ticket/${ticketId}`).then(res => res.data),

  getMyBids: (): Promise<ApiResponse<Bid[]>> =>
    api.get('/bids/my-bids').then(res => res.data),

  acceptOffer: (bidId: string): Promise<ApiResponse<any>> =>
    api.put(`/bids/${bidId}/accept`).then(res => res.data),

  rejectOffer: (bidId: string): Promise<ApiResponse<any>> =>
    api.put(`/bids/${bidId}/reject`).then(res => res.data),
};
*/

// Admin API
export const adminAPI = {
  getDashboard: (): Promise<ApiResponse<DashboardStats>> =>
    api.get('/admin/dashboard').then(res => res.data),

  getUsers: (params?: { page?: number; limit?: number; search?: string; role?: string }): Promise<ApiResponse<PaginatedResponse<User>>> =>
    api.get('/admin/users', { params }).then(res => res.data),

  updateUser: (id: string, data: Partial<User>): Promise<ApiResponse<User>> =>
    api.put(`/admin/users/${id}`, data).then(res => res.data),

  deleteUser: (id: string): Promise<ApiResponse<void>> =>
    api.delete(`/admin/users/${id}`).then(res => res.data),

  getEvents: (params?: { page?: number; limit?: number; category?: string }): Promise<ApiResponse<PaginatedResponse<Event>>> =>
    api.get('/admin/events', { params }).then(res => res.data),

  createEvent: (data: CreateEventRequest): Promise<ApiResponse<Event>> =>
    api.post('/admin/events', data).then(res => res.data),

  updateEvent: (id: string, data: Partial<CreateEventRequest>): Promise<ApiResponse<Event>> =>
    api.put(`/admin/events/${id}`, data).then(res => res.data),

  deleteEvent: (id: string): Promise<ApiResponse<void>> =>
    api.delete(`/admin/events/${id}`).then(res => res.data),

  getTickets: (params?: { page?: number; limit?: number; status?: string; eventId?: string }): Promise<ApiResponse<PaginatedResponse<Ticket>>> =>
    api.get('/admin/tickets', { params }).then(res => res.data),

  updateTicket: (id: string, data: Partial<CreateTicketRequest>): Promise<ApiResponse<Ticket>> =>
    api.put(`/admin/tickets/${id}`, data).then(res => res.data),

  createTicket: (data: CreateTicketRequest): Promise<ApiResponse<Ticket>> =>
    api.post('/admin/tickets', data).then(res => res.data),

  deleteTicket: (id: string): Promise<ApiResponse<void>> =>
    api.delete(`/admin/tickets/${id}`).then(res => res.data),

  deleteTicketsBulk: (ticketIds: string[]): Promise<ApiResponse<void>> =>
    api.delete('/admin/tickets/bulk', { data: { ticketIds } }).then(res => res.data),

  banUser: (id: string, banned: boolean): Promise<ApiResponse<User>> =>
    api.patch(`/admin/users/${id}/ban`, { banned }).then(res => res.data),

  toggleUserVerification: (id: string, verified: boolean): Promise<ApiResponse<User>> =>
    api.patch(`/admin/users/${id}/verify`, { verified }).then(res => res.data),

  getPromoters: (): Promise<ApiResponse<User[]>> =>
    api.get('/admin/promoters').then(res => res.data),

  createPromoter: (data: CreatePromoterRequest): Promise<ApiResponse<User>> =>
    api.post('/admin/promoters', data).then(res => res.data),

  getOrganizers: (): Promise<ApiResponse<User[]>> =>
    api.get('/admin/organizers').then(res => res.data),

  assignOrganizer: (eventId: string, organizerId: string): Promise<ApiResponse<Event>> =>
    api.patch(`/admin/events/${eventId}/assign-organizer`, { organizerId }).then(res => res.data),

  getPromoterAnalytics: (promoterId: string, period?: { period: number }): Promise<ApiResponse<any>> =>
    api.get(`/admin/promoters/${promoterId}/analytics`, { params: period }).then(res => res.data),

  getPendingApprovals: (): Promise<ApiResponse<Ticket[]>> =>
    api.get('/admin/ticket-approvals').then(res => res.data),

  approveTicket: (id: string): Promise<ApiResponse<Ticket>> =>
    api.put(`/admin/tickets/${id}/approve`).then(res => res.data),

  rejectTicket: (id: string, reason: string): Promise<ApiResponse<void>> =>
    api.put(`/admin/tickets/${id}/reject`, { reason }).then(res => res.data),
};

// Organizer API
export const organizerAPI = {
  getDashboard: (): Promise<ApiResponse<any>> =>
    api.get('/organizer/dashboard').then(res => res.data),

  getEvents: (params?: { page?: number; limit?: number; status?: string }): Promise<ApiResponse<PaginatedResponse<Event>>> =>
    api.get('/organizer/events', { params }).then(res => res.data),

  getEventAnalytics: (eventId: string): Promise<ApiResponse<any>> =>
    api.get(`/organizer/events/${eventId}/analytics`).then(res => res.data),

  assignPromoters: (eventId: string, promoterIds: string[]): Promise<ApiResponse<any>> =>
    api.post(`/organizer/events/${eventId}/promoters`, { promoterIds }).then(res => res.data),

  removePromoter: (eventId: string, promoterId: string): Promise<ApiResponse<void>> =>
    api.delete(`/organizer/events/${eventId}/promoters/${promoterId}`).then(res => res.data),

  getPromoterSummary: (eventId: string): Promise<ApiResponse<any>> =>
    api.get(`/organizer/events/${eventId}/promoters/summary`).then(res => res.data),

  getPromoterAnalytics: (promoterId: string, period?: string): Promise<ApiResponse<any>> =>
    api.get(`/organizer/promoters/${promoterId}/analytics`, { params: { period } }).then(res => res.data),

  getPromoters: (): Promise<ApiResponse<User[]>> =>
    api.get('/organizer/promoters').then(res => res.data),

  createPromoter: (data: CreatePromoterRequest): Promise<ApiResponse<User>> =>
    api.post('/organizer/promoters', data).then(res => res.data),
};

// Promoter API
export const promoterAPI = {
  getAssignedEvents: (): Promise<ApiResponse<Event[]>> =>
    api.get('/promoter/events').then(res => res.data),

  getTickets: (params?: { page?: number; limit?: number; eventId?: string }): Promise<ApiResponse<PaginatedResponse<Ticket>>> =>
    api.get('/promoter/tickets', { params }).then(res => res.data),

  issueTickets: (data: IssueTicketRequest): Promise<ApiResponse<any>> =>
    api.post('/promoter/issue-tickets', data).then(res => res.data),

  getDashboardStats: (): Promise<ApiResponse<PromoterDashboardStats>> =>
    api.get('/promoter/dashboard').then(res => res.data),
};

// Admin: Update event resale setting
export const updateEventResaleSetting = async (eventId: string, resaleAllowed: boolean) => {
  try {
    const url = `${API_BASE_URL}/admin/events/${eventId}/resale`;
    const body = JSON.stringify({ resaleAllowed });
    
    const response = await fetch(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Failed to update resale setting');
    }
    
    return { success: true, data };
  } catch (error) {
    return { success: false, message: error instanceof Error ? error.message : 'An error occurred' };
  }
};

// Transfer tickets to another user
export const transferTickets = async (ticketIds: string[], recipientEmail: string) => {
  try {
    const url = `${API_BASE_URL}/tickets/transfer`;
    const body = JSON.stringify({ ticketIds, recipientEmail });
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Failed to transfer tickets');
    }
    
    return { success: true, data };
  } catch (error) {
    return { success: false, message: error instanceof Error ? error.message : 'An error occurred' };
  }
};

export default api; 